import { z } from "zod";
/**
 * Task 8.11 — what the cellar adds up to.
 *
 * `project-scope.md` asks for bottles by country, region, type and vintage
 * decade, the cellar's value, and what was spent per year. Two of those carry
 * decisions that were made long before this screen and are binding here.
 *
 * **Nothing is converted.** A lot records its currency and the price paid in
 * it, so value and spend are reported *per currency* rather than as one
 * number — converting means picking a rate and a date, and every choice there
 * is wrong in some view. Hence a list of amounts rather than an amount.
 *
 * **It is an inventory, not a valuation.** The price is what was paid. What
 * the cellar is worth today is between its owner and their insurer, and the
 * application must not appear to have an opinion about it.
 */
/**
 * One row of a distribution.
 *
 * Both counts, because they answer different questions and the difference is
 * the interesting part: fifty-seven French *wines* standing as a hundred and
 * ten *bottles* says something about how this cellar buys that neither number
 * says alone.
 */
export const distributionRowSchema = z.object({
    /**
     * What the row is: a country code, a region's name, a `WineType`, or a
     * decade as its first year. Untranslated — the browser has the words, and
     * a label built on the server would be the second copy of them.
     */
    key: z.string(),
    wines: z.number().int().nonnegative(),
    bottles: z.number().int().nonnegative(),
});
/** What is held, or what was spent, in one currency. */
export const amountSchema = z.object({
    currency: z.string(),
    /**
     * Kronor and öre, summed in öre and divided once at the end so that no
     * running total ever passes through a float. It arrives as a number because
     * a screen is about to render it, which is the same call the rest of the API
     * makes about a `Decimal`.
     */
    amount: z.number(),
    /** How many bottles this sum covers, which is never all of them. */
    bottles: z.number().int().nonnegative(),
});
export const spendYearSchema = amountSchema.extend({
    year: z.number().int(),
});
/**
 * What the numbers above do not cover.
 *
 * On the page rather than in a footnote, because on this cellar it is a third
 * of the record: 37 of 125 lots carry no price and 47 carry no purchase date.
 * A value that quietly covered seventy per cent of the bottles, presented as
 * *the cellar's value*, would be the most misleading thing this application
 * could put on a screen — and the gap is a fact about the spreadsheet these
 * rows came out of rather than a bug to fix.
 */
export const statisticsSilenceSchema = z.object({
    /** Owned bottles whose lot never recorded a price. Gifts, mostly. */
    bottlesWithoutPrice: z.number().int().nonnegative(),
    /**
     * Lots that recorded a price and no purchase date — money that was spent and
     * cannot be put in a year.
     *
     * Deliberately not *every* lot without a date: a lot with no price is
     * already absent from every total here and is counted by the line above, and
     * saying so twice would make the gap look larger than it is. Two rows of the
     * spreadsheet recorded the date as free text, and an invented one would be
     * worse than a number that says so.
     */
    pricedLotsWithoutDate: z.number().int().nonnegative(),
    /** Wines with no vintage and no base vintage, so no decade. */
    winesWithoutVintage: z.number().int().nonnegative(),
});
/**
 * The first year whose closing windows the pace does not reach.
 *
 * `bottles` is every owned bottle whose window closes that year or earlier;
 * `reach` is what the pace drinks between today and that year's end, rounded
 * down. It assumes every bottle drunk is the most urgent one, so the real
 * shortfall is at least this — see `project-scope.md`.
 */
export const paceShortfallSchema = z.object({
    year: z.number().int(),
    bottles: z.number().int().nonnegative(),
    reach: z.number().int().nonnegative(),
});
/**
 * Task 10.20 — how fast this cellar is drunk, set against what is ready.
 *
 * The rules are the decision of 2026-09-26 in `project-scope.md`: drunk and not
 * undone, since the book was opened or over the last year, and no pace said
 * before ninety days.
 */
export const drinkingPaceSchema = z.object({
    /** The first day of the span, `YYYY-MM-DD`, in the cellar's time zone. */
    since: z.string(),
    /** How many days the span covers, today included. */
    days: z.number().int().nonnegative(),
    /** Bottles recorded drunk in the span and not undone. */
    drunk: z.number().int().nonnegative(),
    /**
     * Bottles a year, or null while the span is too short to say one. Null
     * rather than the arithmetic, because a fortnight's arithmetic is a number
     * that looks like a fact.
     */
    perYear: z.number().nonnegative().nullable(),
    /** Owned bottles whose window is open now: ready, or at their peak. */
    ready: z.number().int().nonnegative(),
    /** Owned bottles whose window has closed. */
    past: z.number().int().nonnegative(),
    /** Owned bottles with no window to judge, and so in neither count. */
    withoutWindow: z.number().int().nonnegative(),
    /** Null when there is no pace yet, or when the pace reaches every year. */
    shortfall: paceShortfallSchema.nullable(),
});
/**
 * Task 10.22 — one year of what was drunk, drawn the way what is owned is.
 *
 * The same four distributions, over the bottles drunk that year: a bottle is
 * counted once, in the year it was drunk, and a wine is counted once per year
 * however many of its bottles went.
 */
export const drunkYearSchema = z.object({
    year: z.number().int(),
    bottles: z.number().int().nonnegative(),
    wines: z.number().int().nonnegative(),
    byCountry: distributionRowSchema.array(),
    byType: distributionRowSchema.array(),
    byRegion: distributionRowSchema.array(),
    byDecade: distributionRowSchema.array(),
});
/**
 * What was drunk, by year, since the book was opened — the decision of
 * 2026-09-26 in `project-scope.md`, on the pace's rules.
 */
export const drunkByYearSchema = z.object({
    /** The day the book was opened, `YYYY-MM-DD`: nothing before it is here. */
    since: z.string(),
    /** Newest first. Empty until something is drunk. */
    years: drunkYearSchema.array(),
    /**
     * Bottles drunk before the book was opened — the spreadsheet's, which it
     * recorded as a count rather than a day — and so in no year.
     */
    beforeTheBook: z.number().int().nonnegative(),
});
export const cellarStatisticsSchema = z.object({
    /** Owned: standing in the cellar, or out and coming back. */
    wines: z.number().int().nonnegative(),
    bottlesInCellar: z.number().int().nonnegative(),
    bottlesAway: z.number().int().nonnegative(),
    byCountry: distributionRowSchema.array(),
    byRegion: distributionRowSchema.array(),
    byType: distributionRowSchema.array(),
    byDecade: distributionRowSchema.array(),
    /**
     * Null where the caller may not see prices.
     *
     * `project-scope.md`: purchase price, lot data and cellar value are hidden
     * from guests, with a per-cellar setting to reveal them. Null rather than
     * zero or an empty list, so the screen can say *not shown* instead of
     * *nothing* — of the two ways to be wrong about money, saying less is the
     * recoverable one.
     */
    value: amountSchema.array().nullable(),
    spend: spendYearSchema.array().nullable(),
    silence: statisticsSilenceSchema,
    /**
     * Null for anybody but the owner: how fast somebody drinks is a fact about
     * them rather than about the cellar, the decision of 2026-09-26.
     *
     * It was additive for one release, per CLAUDE.md, and was paid on
     * 2026-09-26 once revision 50 was serving it. Null is still an answer, so
     * only the default came off: a response without the field is now a broken
     * deploy rather than a reader who is not the owner.
     */
    pace: drinkingPaceSchema.nullable(),
    /**
     * Null for anybody but the owner, for the pace's reason: what somebody drank
     * and when is a fact about them.
     *
     * It was additive for one release, per CLAUDE.md, and was paid the morning
     * it fell due, once revision 52 was serving it. Null is still an answer, so
     * only the default came off.
     */
    drunk: drunkByYearSchema.nullable(),
});
//# sourceMappingURL=statistics.js.map